import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppIcon } from "@/components/AppIcon";
import { LOGIN } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoCluster } from "@/components/LogoBadges";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627920769842-6887c6df05ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGFncmljdWx0dXJlJTIwc2Vuc29yfGVufDB8fHx8MTc4MDU1NjA3Mnww&ixlib=rb-4.1.0&q=85";

/**
 * Public per-tenant login page: /login/p/<projectId>
 * Fetches the project's merged branding (with global fallback) and renders the login form
 * skinned to that tenant. After successful login, navigates to the dashboard with the
 * chosen project pre-selected.
 */
export default function TenantLogin() {
  const { projectId } = useParams();
  const [branding, setBranding] = useState(null);
  const [error404, setError404] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from?.pathname || "/";

  useEffect(() => {
    api.get(`/projects/${projectId}/branding`)
      .then((r) => setBranding(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setError404(true);
        else setBranding({ login: { app_name: "CINNABIT", description: "", extra_info: "", icon_url: null } });
      });
  }, [projectId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      localStorage.setItem("cinnabit_project", projectId);
      toast.success("Login berhasil");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error404) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-grain text-center">
        <AlertTriangle className="w-10 h-10 text-destructive mb-4" />
        <h1 className="font-display text-2xl font-bold">Proyek tidak ditemukan</h1>
        <p className="mt-2 text-muted-foreground">ID proyek <span className="font-mono">{projectId}</span> tidak ada.</p>
        <Link to="/login" className="mt-6 text-primary underline-offset-4 hover:underline">Kembali ke login utama</Link>
      </div>
    );
  }

  if (!branding) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <span className="overline">Memuat tenant…</span>
      </div>
    );
  }

  const b = branding.login;
  const projectName = branding.project_name;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="Smart agriculture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-stone-900/85 via-stone-900/45 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <AppIcon url={b.icon_url} size={40} className="bg-white/15 backdrop-blur" />
            <div className="font-display font-bold text-xl">{b.app_name}</div>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">{b.extra_info || `Tenant · ${projectName}`}</div>
            <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
              {b.description || "Selamat datang di CINNABIT."}
            </h2>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/70">
              <span className="status-dot bg-white/70" /> Mode tenant: {projectName}
            </div>
            <div className="mt-6 flex items-center gap-2">
              <LogoCluster />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <AppIcon url={b.icon_url} size={40} />
            <div className="font-display font-bold text-xl">{b.app_name}</div>
          </div>
          <div className="overline text-primary mb-3">Tenant · {projectName}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Masuk ke akun Anda</h1>
          <p className="mt-2 text-sm text-muted-foreground">{b.description}</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid={LOGIN.emailInput} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid={LOGIN.passwordInput} />
            </div>
            {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
            <Button type="submit" className="w-full" disabled={submitting} data-testid={LOGIN.submitButton}>
              {submitting ? "Masuk…" : "Masuk"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-muted-foreground">
            Bukan tenant ini? <Link to="/login" className="text-primary underline-offset-4 hover:underline">Login utama</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
